import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { GroupValuePoint } from "../../lib/otterfolio";
import { ToggleGroup } from "./ToggleGroup";
import { OTTERFOLIO_THEME } from "./theme";

type Range = "range30d" | "range90d" | "rangeSinceStart";

const RANGE_KEY: Record<Range, "last_30d" | "last_90d" | "since_start"> = {
  range30d: "last_30d",
  range90d: "last_90d",
  rangeSinceStart: "since_start",
};

interface Props {
  valueHistory: {
    since_start: GroupValuePoint[];
    last_30d: GroupValuePoint[];
    last_90d: GroupValuePoint[];
  };
  benchmarkLabel: string | null;
}

export function ValueChartCard({ valueHistory, benchmarkLabel }: Props) {
  const { t } = useTranslation();
  const [range, setRange] = useState<Range>("rangeSinceStart");
  const [showBenchmark, setShowBenchmark] = useState(true);
  const data = valueHistory[RANGE_KEY[range]].map((p) => ({
    date: p.date,
    total: p.totalCents / 100,
    fels: p.felsCents / 100,
    rakete: p.raketeCents / 100,
    benchmark: p.benchmarkCents !== null ? p.benchmarkCents / 100 : null,
  }));
  const hasBenchmark = benchmarkLabel !== null && data.some((d) => d.benchmark !== null);
  const renderBenchmark = hasBenchmark && showBenchmark;

  return (
    <div
      style={{
        background: OTTERFOLIO_THEME.cardBg,
        border: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
        borderRadius: 8,
        padding: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              color: OTTERFOLIO_THEME.primary,
              textTransform: "uppercase",
              letterSpacing: 1,
              fontWeight: 700,
            }}
          >
            {t("otterfolio.valueChart")}
          </div>
          {hasBenchmark && (
            <button
              type="button"
              onClick={() => setShowBenchmark((v) => !v)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                marginTop: 2,
                background: "transparent",
                border: 0,
                padding: 0,
                cursor: "pointer",
                fontSize: 10,
                color: OTTERFOLIO_THEME.textMuted,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  width: 14,
                  height: 3,
                  background: showBenchmark ? OTTERFOLIO_THEME.highlight : "transparent",
                  border: `1px solid ${OTTERFOLIO_THEME.highlight}`,
                  borderRadius: 2,
                }}
              />
              {t("otterfolio.benchmarkLabel", {
                defaultValue: "Referenz: {{name}}",
                name: benchmarkLabel,
              })}
            </button>
          )}
        </div>
        <ToggleGroup
          options={[
            { key: "range30d", label: t("otterfolio.range30d") },
            { key: "range90d", label: t("otterfolio.range90d") },
            { key: "rangeSinceStart", label: t("otterfolio.rangeSinceStart") },
          ]}
          active={range}
          onChange={(k) => setRange(k as Range)}
        />
      </div>
      <div style={{ height: 175 }}>
        <ResponsiveContainer>
          <ComposedChart data={data}>
            <defs>
              <linearGradient id="otterfolioGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={OTTERFOLIO_THEME.primary} stopOpacity={0.4} />
                <stop offset="100%" stopColor={OTTERFOLIO_THEME.primary} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tick={{ fill: OTTERFOLIO_THEME.labelMuted, fontSize: 9 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: OTTERFOLIO_THEME.labelMuted, fontSize: 9 }}
              axisLine={false}
              tickLine={false}
              width={50}
              domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.05)]}
            />
            <Tooltip
              contentStyle={{
                background: OTTERFOLIO_THEME.bg,
                border: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
                fontSize: 11,
              }}
              labelStyle={{ color: OTTERFOLIO_THEME.textMuted }}
              formatter={(v: number) => [
                `${v.toLocaleString("de-DE", { maximumFractionDigits: 2 })} €`,
                "",
              ]}
            />
            <Line
              type="monotone"
              dataKey="fels"
              stroke={OTTERFOLIO_THEME.fels}
              strokeOpacity={0.35}
              strokeDasharray="3 3"
              strokeWidth={1.2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="rakete"
              stroke={OTTERFOLIO_THEME.rakete}
              strokeOpacity={0.35}
              strokeDasharray="3 3"
              strokeWidth={1.2}
              dot={false}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="total"
              stroke={OTTERFOLIO_THEME.primary}
              strokeWidth={2.5}
              fill="url(#otterfolioGradient)"
              isAnimationActive={false}
            />
            {renderBenchmark && (
              <Line
                type="monotone"
                dataKey="benchmark"
                stroke={OTTERFOLIO_THEME.highlight}
                strokeWidth={2.5}
                strokeDasharray="6 4"
                dot={{
                  r: 3,
                  fill: OTTERFOLIO_THEME.highlight,
                  stroke: OTTERFOLIO_THEME.bg,
                  strokeWidth: 1,
                }}
                activeDot={{ r: 4 }}
                connectNulls
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
